/**
 * AJAX Filter — Replaces page reload with seamless AJAX content swap.
 * Works on both collection and search pages.
 *
 * Targets:
 *  Collection: #filter-form, #collection-results, #collection-toolbar, #filter-sidebar
 *  Search:     #search-filter-form, #search-results, #search-toolbar, #search-filter-sidebar
 */
(function () {
  'use strict';

  /* ── Detect page context ── */
  var isSearch = !!document.getElementById('search-filter-form');
  var formId = isSearch ? 'search-filter-form' : 'filter-form';
  var resultsId = isSearch ? 'search-results' : 'collection-results';
  var toolbarId = isSearch ? 'search-toolbar' : 'collection-toolbar';
  var sidebarId = isSearch ? 'search-filter-sidebar' : 'filter-sidebar';

  var form = document.getElementById(formId);
  if (!form) return; // Not a filterable page

  var loading = false;
  // Rapid-click handling — see fetchAndReplace() and bindCheckboxes() below.
  var currentController = null;
  var filterDebounce = null;

  // Last URL whose results are currently rendered. Used to ignore popstate
  // events that don't change the URL (the cart drawer pushes/pops a same-URL
  // history entry when opening/closing).
  var renderedUrl = window.location.href;

  /* ── Build URL from form data ── */
  function buildURL() {
    var formData = new FormData(form);
    var params = new URLSearchParams();

    for (var pair of formData.entries()) {
      if (pair[1] !== '' && pair[1] !== null) {
        params.append(pair[0], pair[1]);
      }
    }

    // Preserve sort_by from whichever select is present
    var sortSelect = isSearch
      ? (document.getElementById('search-sort-by-sidebar') || document.getElementById('search-sort-by'))
      : (document.getElementById('sort-by-sidebar') || document.getElementById('sort-by'));
    if (sortSelect && sortSelect.value) {
      params.set('sort_by', sortSelect.value);
    }

    return window.location.pathname + '?' + params.toString();
  }

  /* ── Fetch + swap content ── */
  function fetchAndReplace(url, pushState) {
    // This used to be `if (loading) return;`, which silently DROPPED any filter
    // change made while a request was in flight. Ticking three brands quickly
    // applied only the first; the other two were then visually un-ticked when
    // the response overwrote the sidebar (step 3 below). Do not reinstate it.
    //
    // Instead: cancel the in-flight request so the newest selection always wins
    // and a stale response can never overwrite newer state.
    if (currentController) currentController.abort();
    currentController = new AbortController();
    var controller = currentController;
    loading = true;

    document.dispatchEvent(new CustomEvent('ajax:filter:start'));

    // Show loading state
    var results = document.getElementById(resultsId);
    if (results) {
      results.style.opacity = '0.3';
      results.style.pointerEvents = 'none';
      results.style.transition = 'opacity 0.15s ease';
    }

    fetch(url, { credentials: 'same-origin', signal: controller.signal })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        // 1. Replace results (grid + pagination)
        var newResults = doc.getElementById(resultsId);
        var oldResults = document.getElementById(resultsId);
        if (newResults && oldResults) {
          oldResults.innerHTML = newResults.innerHTML;
          oldResults.style.pointerEvents = '';

          // Animate product cards in with stagger
          var cards = oldResults.querySelectorAll('.product-grid-item');
          oldResults.style.opacity = '1';
          cards.forEach(function (card, i) {
            card.style.opacity = '0';
            card.style.transform = 'translateY(16px)';
            card.style.transition = 'none';
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                card.style.transition = 'opacity 0.35s ease, transform 0.35s ease';
                card.style.transitionDelay = (i * 40) + 'ms';
                card.style.opacity = '1';
                card.style.transform = 'translateY(0)';
              });
            });
          });
        }

        // 2. Replace toolbar (active filter tags + sort) — full element swap to update style
        var newToolbar = doc.getElementById(toolbarId);
        var oldToolbar = document.getElementById(toolbarId);
        if (newToolbar && oldToolbar) {
          oldToolbar.replaceWith(newToolbar.cloneNode(true));
          // Re-bind sort select
          bindSortSelects();
        }

        // 3. Replace filter sidebar content (updated counts)
        var newSidebar = doc.getElementById(sidebarId);
        var oldSidebar = document.getElementById(sidebarId);
        if (newSidebar && oldSidebar) {
          // Preserve open/close state of filter groups
          var openStates = {};
          oldSidebar.querySelectorAll('.filter-group-title, .search-filter-group-title').forEach(function (t) {
            var content = t.nextElementSibling;
            if (content) {
              openStates[t.textContent.trim()] = content.style.display !== 'none';
            }
          });

          // Get the form from the new sidebar
          var newForm = newSidebar.querySelector('#' + formId);
          var oldForm = document.getElementById(formId);
          if (newForm && oldForm) {
            oldForm.innerHTML = newForm.innerHTML;
            // Re-bind form submit
            bindFormSubmit();
            // Re-bind filter group toggles
            bindFilterToggles();
            // Re-bind checkbox onchange
            bindCheckboxes();
            // Re-bind show more/less buttons
            bindShowMore();
            // Sidebar sort_by lives inside the form — its old element was
            // wiped by the innerHTML swap, so re-attach the change handler.
            bindSortSelects();

            // Restore open/close states
            oldForm.querySelectorAll('.filter-group-title, .search-filter-group-title').forEach(function (t) {
              var key = t.textContent.trim();
              var content = t.nextElementSibling;
              var chevron = t.querySelector('.filter-chevron, .search-filter-chevron');
              if (content && openStates[key] === false) {
                content.style.display = 'none';
                if (chevron) chevron.style.transform = 'rotate(-90deg)';
              }
            });
          }

        }

        // 4. Replace filter heading (product count, active count badge)
        var headingClass = isSearch ? '.search-filter-desktop-heading' : '.filter-desktop-heading';
        var newHeading = doc.querySelector(headingClass);
        var oldHeading = document.querySelector(headingClass);
        if (newHeading && oldHeading) {
          oldHeading.innerHTML = newHeading.innerHTML;
        }

        // 5. Update URL (must happen BEFORE ajax:filter:done so vendor enhancement reads correct URL)
        if (pushState !== false) {
          history.pushState({ ajaxFilter: true }, '', url);
        }
        renderedUrl = window.location.href;

        // 6. Re-run vendor filter enhancement (sorts active first, adds show more/less)
        document.dispatchEvent(new CustomEvent('ajax:filter:done'));

        // 6. Scroll to top of results
        var scrollTarget = document.getElementById(toolbarId) || document.getElementById(resultsId);
        if (scrollTarget) {
          var offset = scrollTarget.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: offset, behavior: 'smooth' });
        }

        // 7. Close mobile filter drawer if open
        var sidebar = document.getElementById(sidebarId);
        var overlay = document.querySelector('.filter-overlay, .search-filter-overlay');
        if (sidebar) sidebar.classList.remove('is-open');
        if (overlay) overlay.classList.remove('is-open');
        document.body.style.overflow = '';

        loading = false;
      })
      .catch(function (err) {
        // A superseded request aborting is normal, not an error. Falling through
        // to the navigation fallback here would hard-load the OLD url and throw
        // away the selection the user just made.
        if (err && err.name === 'AbortError') return;
        console.error('[AjaxFilter]', err);
        // Fallback: regular navigation
        window.location.href = url;
        loading = false;
      });
  }

  /* ── Bind form submit handler ── */
  function bindFormSubmit() {
    var f = document.getElementById(formId);
    if (!f) return;

    // Remove any old listeners by cloning (simple dedup)
    f._ajaxBound = true;

    f.addEventListener('submit', handleFormSubmit);
  }

  function handleFormSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    var url = buildURL();
    fetchAndReplace(url);
  }

  /* ── Bind checkbox onchange (re-dispatch submit) ── */
  function bindCheckboxes() {
    var f = document.getElementById(formId);
    if (!f) return;

    f.querySelectorAll('input[type="checkbox"]').forEach(function (cb) {
      cb.setAttribute('onchange', '');
      cb.onchange = function () {
        // Coalesce rapid ticks into a single request. buildURL() reads live DOM
        // state at fire time, so one fetch carries every box ticked during the
        // window — and the sidebar isn't swapped mid-click, so nothing reverts.
        // filterDebounce is module-scoped so it survives the re-bind that
        // happens after each swap.
        clearTimeout(filterDebounce);
        filterDebounce = setTimeout(function () {
          f.dispatchEvent(new Event('submit', { cancelable: true }));
        }, 300);
      };
    });
  }

  /* ── Bind sort select changes ── */
  function bindSortSelects() {
    document.querySelectorAll('[data-ajax-sort]').forEach(function (sel) {
      sel.onchange = function () {
        // Update sort in form's hidden or select element
        var f = document.getElementById(formId);
        if (f) {
          var hiddenSort = f.querySelector('input[name="sort_by"]');
          if (hiddenSort) {
            hiddenSort.value = sel.value;
          }
          // Also sync all sort selects
          document.querySelectorAll('[data-ajax-sort]').forEach(function (s) {
            s.value = sel.value;
          });
        }
        var url = buildURL();
        fetchAndReplace(url);
      };
    });
  }

  /* ── Bind filter group collapsible toggles ── */
  function bindFilterToggles() {
    var titleClass = isSearch ? '.search-filter-group-title' : '.filter-group-title';
    var chevronClass = isSearch ? '.search-filter-chevron' : '.filter-chevron';

    document.querySelectorAll(titleClass).forEach(function (title) {
      title.onclick = function () {
        var content = this.nextElementSibling;
        var chevron = this.querySelector(chevronClass);
        if (content) {
          var isHidden = content.style.display === 'none';
          content.style.display = isHidden ? '' : 'none';
          if (chevron) chevron.style.transform = isHidden ? '' : 'rotate(-90deg)';
        }
      };
    });
  }

  /* ── Bind show more / show less buttons for filter values ── */
  function bindShowMore() {
    var btnSelector = isSearch ? '[data-search-show-more]' : '[data-show-more]';
    var contentClass = isSearch ? '.search-filter-group-content' : '.filter-group-content';
    var hiddenClass = isSearch ? '.search-filter-value-hidden' : '.filter-value-hidden';

    document.querySelectorAll(btnSelector).forEach(function (btn) {
      var filterGroup = btn.closest(contentClass);
      if (!filterGroup) return;
      var hiddenItems = filterGroup.querySelectorAll(hiddenClass);
      var showMoreText = btn.textContent.trim();
      var showLessText = isSearch ? 'Show Less' : 'Show Less';
      var expanded = false;

      btn.onclick = function () {
        expanded = !expanded;
        hiddenItems.forEach(function (item) {
          item.style.display = expanded ? 'flex' : 'none';
        });
        btn.textContent = expanded ? showLessText : showMoreText;
      };
    });
  }

  /* ── Handle active filter tag X clicks via AJAX ── */
  function bindActiveFilterLinks() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('.active-filter-tag a, .search-active-filter-tag a');
      if (!link) return;

      // Also catch "Clear all" links
      var clearLink = e.target.closest('a[href]');
      if (clearLink) {
        var href = clearLink.getAttribute('href');
        // Only intercept filter-related URLs (same origin, no external links)
        if (href && (href.indexOf('filter.') > -1 || href === window.location.pathname || href.indexOf('?') === -1)) {
          e.preventDefault();
          fetchAndReplace(href);
        }
      }
    });
  }

  /* ── Handle pagination clicks via AJAX ── */
  function bindPaginationLinks() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('.collection-pagination a, .search-pagination a');
      if (!link) return;
      e.preventDefault();
      fetchAndReplace(link.href);
    });
  }

  /* ── Handle browser back/forward ── */
  window.addEventListener('popstate', function (e) {
    // Ignore pops that don't change the URL — e.g. the cart drawer consuming
    // its own history entry on close. Re-fetching here wiped the grid and
    // smooth-scrolled the page away from where the user was.
    if (window.location.href === renderedUrl) return;
    if (document.getElementById(resultsId)) {
      fetchAndReplace(window.location.href, false);
    }
  });

  /* ── Init ── */
  document.addEventListener('DOMContentLoaded', function () {
    if (!document.getElementById(formId)) return;

    bindFormSubmit();
    bindCheckboxes();
    bindSortSelects();
    bindActiveFilterLinks();
    bindPaginationLinks();
  });
})();
